import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { RecommendationPageMode, FoliarApplicationAdvice } from '../types';
import { FoliarForm } from '../components/Forms/FoliarForm/FoliarForm';
import type { FoliarFormRef } from '../components/Forms/FoliarForm/FoliarForm';
import { FOLIAR_TITLES, FOLIAR_SUBTITLES } from '../constants/page.constants';
import { PanelActions } from '../components/PanelActions/PanelActions';
import styles from './FoliarAdvicePage.module.css';

export interface FoliarAdvicePageProps {
	onBack: () => void;
	initialMode?: RecommendationPageMode;
	viewData?: FoliarApplicationAdvice;
	adviceId?: string;
	onDelete?: () => void;
	canEdit?: boolean;
}

const getId = (value: unknown): string => {
	if (!value) return '';

	if (typeof value === 'string') return value;

	if (typeof value === 'object' && '_id' in value) {
		return String((value as { _id?: string })._id ?? '');
	}

	return '';
};

export const FoliarAdvicePage = ({
	onBack,
	initialMode = 'add',
	viewData: initialViewData,
	adviceId,
	onDelete,
	canEdit = true,
}: FoliarAdvicePageProps) => {
	const [currentMode, setCurrentMode] = useState<RecommendationPageMode>(initialMode);
	const [viewData, setViewData] = useState<FoliarApplicationAdvice | undefined>(initialViewData);
	const [isLoading, setIsLoading] = useState(false);

	const foliarRef = useRef<FoliarFormRef>(null);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess } = useToast();

	useEffect(() => {
		setViewData(initialViewData);
	}, [initialViewData]);

	const handleSubmit = useCallback((advice: FoliarApplicationAdvice) => {
		setViewData(advice);
		setCurrentMode('view');
	}, []);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			if (currentMode === 'edit' && initialMode === 'view') {
				setCurrentMode('view');
				return;
			}
			onBack();
		};

		if (!foliarRef.current?.isDirty?.()) {
			revert();
			return;
		}

		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});

		if (!confirmed) return;

		revert();
	}, [confirm, currentMode, initialMode, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir recomendação',
			message: 'Tem certeza que deseja excluir esta recomendação? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});

		if (!confirmed) return;

		toastSuccess('Recomendação excluída com sucesso.');
		onDelete?.();
	}, [confirm, onDelete, toastSuccess]);

	const handleEdit = useCallback(() => {
		setCurrentMode('edit');
	}, []);

	const handleSave = useCallback(() => {
		foliarRef.current?.submit();
	}, []);

	const editInitialData = useMemo(
		() =>
			currentMode === 'edit' && viewData
				? {
						name: viewData.name ?? '',
						prop: getId(viewData.prop),
						area: getId(viewData.area),
						sectors: viewData.sectors?.map(getId) ?? [],
						applicationDate: viewData.applicationDate?.split('T')[0] ?? '',
						tankVolume: viewData.tankVolume ?? 0,
						syrupVolume: viewData.syrupVolume ?? 0,
						tanksToPrepare: viewData.tanksToPrepare ?? 0,
						products:
							viewData.products?.map((p) => ({
								productClass: p.productClass ?? '',
								productCommercialName: getId(p.productCommercialName),
								productTradeMark: p.productTradeMark ?? '',
								dose: String(p.dose ?? ''),
								doseMeasurementUnit: (p.doseMeasurementUnit ?? '/ha') as '/ha' | '/100L',
							})) ?? [],
					}
				: undefined,
		[currentMode, viewData]
	);

	const pageActions = (
		<PanelActions>
			<FormActions
				mode={currentMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isLoading}
				onDelete={onDelete ? handleDelete : undefined}
				onEdit={canEdit ? handleEdit : undefined}
			/>
		</PanelActions>
	);

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<PageHeader
					title={FOLIAR_TITLES[currentMode]}
					subtitle={FOLIAR_SUBTITLES[currentMode]}
					onBack={onBack}
				/>

				<div className={styles.cards}>
					<div className={styles.mainContent}>
						<FormCard
							icon={<FormatListBulletedOutlined />}
							title="Informações Gerais"
							className={styles.mainCard}
						>
							<FoliarForm
								key={`${currentMode}-${adviceId ?? 'new'}`}
								ref={foliarRef}
								mode={currentMode}
								initialData={editInitialData}
								viewData={viewData}
								adviceId={adviceId}
								onSubmit={handleSubmit}
								onLoadingChange={setIsLoading}
							/>
						</FormCard>

						{pageActions}
					</div>
				</div>

				<ConfirmModal
					isOpen={isConfirmOpen}
					title={confirmOptions.title}
					message={confirmOptions.message}
					confirmText={confirmOptions.confirmText}
					cancelText={confirmOptions.cancelText}
					variant={confirmOptions.variant}
					onConfirm={handleConfirm}
					onCancel={handleConfirmCancel}
				/>
			</div>
		</div>
	);
};
